import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { FaCaretDown } from 'react-icons/fa';
import { appConfig } from 'config/appConfig';
import * as routes from 'constants/routes';
import { withAuthState } from 'hoc/auth';
import { useOnClickOutside } from 'hooks';
import cx from 'classnames';
import { FiBell, FiBook, FiLogOut, FiMap, FiMenu, FiUser, FiUsers } from 'react-icons/fi';

const PROFILE_REGEX = /\/((profile|profile\/edit))$/;

const Header = ({ loggedUserInfo, logoutHandler }) => {
  const dropmenuRef = useRef();
  const sidenavRef = useRef();

  const location = useLocation();
  const [isMenuShown, setIsMenuShown] = useState(false);
  const isSuperAdmin = loggedUserInfo.isSuperAdmin;
  const homeRoute = isSuperAdmin ? routes.ADMIN_EXPERIENCES : routes.CLIENT_EXPERIENCES;
  const profileRoute = isSuperAdmin ? routes.ADMIN_PROFILE : routes.CLIENT_PROFILE;

  useOnClickOutside(dropmenuRef, () => setIsMenuShown(false));
  const userFirstName = loggedUserInfo.fullName.split(' ')[0];
  const isProfileLinkActive = PROFILE_REGEX.test(location.pathname);
  const [sidenav, showSidenav] = useState(false);

  const toggleSideNav = event => {
    event.preventDefault();
    showSidenav(!sidenav);
  };

  const setDocumentHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  useEffect(() => {
    setDocumentHeight();
    window.addEventListener('resize', setDocumentHeight);
  }, []);

  return (
    <nav className="navbar navbar--bordered-bottom navbar--sticky">
      <div className="container-fluid">
        <div className="navbar__wrap navbar__wrap--content-spread">
          <div className="sidenav">
            <button
              className="hamburger-menu btn btn--iconOnly"
              onClick={event => toggleSideNav(event)}
              ref={sidenavRef}
            >
              <FiMenu size={24} />
            </button>

            <div className={`sidenav__bg ${sidenav ? 'show' : ''}`}>
              <div className={`sidenav__wrapper ${sidenav ? 'show' : ''}`} ref={sidenavRef}>
                {sidenav && (
                  <div className="sidenav__left-item">
                    <div className="sidenav__wrapper--logo">
                      <NavLink to={homeRoute}>
                        <img src={appConfig.logo} alt="logo" className="navbar__logo" />
                      </NavLink>
                    </div>
                    <div className="sidenav__link">
                      <NavLink className="sidenav__link--wrapper" to={homeRoute}>
                        <FiMap />
                        Adventures
                      </NavLink>
                      {isSuperAdmin && (
                        <>
                          <NavLink className="sidenav__link--wrapper" to={routes.ADMIN_CLIENTS}>
                            <FiBook />
                            Whoa!Guides
                          </NavLink>
                          <NavLink className="sidenav__link--wrapper" to={routes.APP_USERS}>
                            <FiUsers />
                            Users
                          </NavLink>
                        </>
                      )}
                    </div>
                    <div className="sidenav__staging">
                      <NavLink className="sidenav__link--wrapper" to={profileRoute}>
                        <FiUser />
                        Profile
                      </NavLink>
                    </div>
                    <div className="sidenav__footer">
                      <div className="sidenav__link--wrapper" onClick={logoutHandler}>
                        <FiLogOut />
                        Log Out
                      </div>
                    </div>
                  </div>
                )}

                <div className="sidenav__right-item" onClick={event => toggleSideNav(event)}></div>
              </div>
            </div>
          </div>
          <div className="navbar__left">
            <NavLink to={homeRoute}>
              <img src={appConfig.logo} alt="logo" className="navbar__logo" />
            </NavLink>
          </div>
          <div className="navbar__right d-flex align-items-center d-none-sm">
            <div className="nav">
              <div className="nav__node">
                <NavLink to={homeRoute}>Adventures</NavLink>
              </div>
            </div>
            {isSuperAdmin && (
              <>
                <div className="nav">
                  <div className="nav__node">
                    <NavLink to={routes.ADMIN_CLIENTS}>Whoa!Guides</NavLink>
                  </div>
                </div>
                <div className="nav">
                  <div className="nav__node">
                    <NavLink to={routes.APP_USERS}>Users</NavLink>
                  </div>
                </div>
              </>
            )}

            <div className="nav">
              <div
                ref={dropmenuRef}
                className={cx('nav__node navbar__right-logout d-flex align-items-center cursor-pointer', {
                  'lf-color-black-base': isProfileLinkActive,
                })}
                onClick={() => setIsMenuShown(!isMenuShown)}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {!loggedUserInfo.isSuperAdmin && loggedUserInfo.profileImg && (
                    <div className="mr-2x avatar avatar--xs avatar--round">
                      <img src={loggedUserInfo.profileImg} alt="ProfileImg" />
                    </div>
                  )}
                  <p className="lf-font-weight--normal lf-text-capitalized">{userFirstName}</p>
                </div>
                <FaCaretDown />
                <div className={classnames('dropmenu', { show: isMenuShown })}>
                  <div className="dropmenu__node dropmenu__vertical">
                    <Link className="dropmenu__link-item" to={profileRoute}>
                      Profile
                    </Link>
                    {isSuperAdmin ? (
                      <Link className="dropmenu__link-item" to={routes.ADMIN_PAYMENT}>
                        Payments
                      </Link>
                    ) : (
                      <Link className="dropmenu__link-item" to={routes.CLIENT_PAYMENT}>
                        Payments
                      </Link>
                    )}
                    <p className="dropmenu__link-item" onClick={logoutHandler}>
                      Log Out
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="notification__wrapper--sm">
            {isSuperAdmin && (
              <>
                <Link className="notification__bell" to={routes.NOTIFICATION_SM}>
                  <FiBell />
                  <div className="notification--badge">10</div>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

Header.propTypes = {
  loggedUserInfo: PropTypes.object.isRequired,
  logoutHandler: PropTypes.func.isRequired,
};

export default withAuthState(Header);
